import { getLoggedInUser } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useMemo } from 'react';
// import { useAuth } from '../contexts/AuthContext'; // Adjust import path as needed

// Type definitions for the feature structure
export interface FeatureItem {
	tooltip?: string;
	displayText?: string;
	value: boolean | number | string;
}

export interface FeatureCategory {
	[key: string]: FeatureItem;
}

export interface Features {
	AnalyticsAndReporting?: FeatureCategory;
	supportAndSLA?: FeatureCategory;
	knowledgeAndCRM?: FeatureCategory;
	phoneNumbersAndForwarding?: FeatureCategory;
	advancedCapabilities?: FeatureCategory;
	workflows?: FeatureCategory;
	communicationAndCallHandling?: FeatureCategory;
	[key: string]: FeatureCategory | undefined;
}

// Feature gate types
export interface FeatureGateProps {
	featureKey: string;
	categoryKey?: string;
	fallbackMessage?: string;
	actionButton?: {
		text: string;
		onClick: () => void;
		variant?: 'primary' | 'secondary' | 'outline';
	};
	children: React.ReactNode;
	showLockIcon?: boolean;
	className?: string;
}

// Hook return type
export interface UseFeatureGatingReturn {
	features: Features | null;
	isLoading: boolean;
	error: string | null;
	isFeatureEnabled: (featureKey: string, categoryKey?: string) => boolean;
	getFeatureValue: (featureKey: string, categoryKey?: string) => any;
	getFeatureDisplayText: (featureKey: string, categoryKey?: string) => string;
	getFeatureTooltip: (featureKey: string, categoryKey?: string) => string;
	hasFeature: (featureKey: string, categoryKey?: string) => boolean;
	isFeatureUnlimited: (featureKey: string, categoryKey?: string) => boolean;
	getFeatureNumericValue: (featureKey: string, categoryKey?: string) => number;
	isFeatureNumericEnabled: (featureKey: string, categoryKey?: string, minValue?: number) => boolean;
	getCurrentPlan: () => string;
}

export const useFeatureGating = (): UseFeatureGatingReturn => {
	const user = useAppSelector(getLoggedInUser);

	const features = useMemo(() => {
		if (!user?.features) {
			return null;
		}
		return user.features as Features;
	}, [user?.features]);

	const isLoading = !user;
	const error = null;

	const isFeatureEnabled = (featureKey: string, categoryKey?: string): boolean => {
		if (!features) {
			return true;
		}

		if (categoryKey) {
			const category = features[categoryKey];
			if (!category) {
				return true;
			}

			const feature = category[featureKey];
			if (!feature) {
				return true;
			}

			// Handle different value types
			if (typeof feature.value === 'boolean') {
				return feature.value;
			}
			if (typeof feature.value === 'number') {
				return feature.value > 0 || feature.value === -1; // -1 means unlimited
			}

			return Boolean(feature.value);
		}

		// Search across all categories
		for (const category of Object.values(features)) {
			if (category && category[featureKey]) {
				const feature = category[featureKey];
				if (typeof feature.value === 'boolean') {
					return feature.value;
				}
				if (typeof feature.value === 'number') {
					return feature.value > 0 || feature.value === -1;
				}

				return Boolean(feature.value);
			}
		}

		return false;
	};

	const getFeatureValue = (featureKey: string, categoryKey?: string): any => {
		if (!features) {
			return null;
		}

		if (categoryKey) {
			const category = features[categoryKey];
			if (!category) {
				return null;
			}

			const feature = category[featureKey];
			return feature?.value ?? null;
		}

		// Search across all categories
		for (const category of Object.values(features)) {
			if (category && category[featureKey]) {
				return category[featureKey].value;
			}
		}

		return null;
	};

	const getFeatureNumericValue = (featureKey: string, categoryKey?: string): number => {
		const value = getFeatureValue(featureKey, categoryKey);
		if (typeof value === 'number') {
			return value;
		}
		if (typeof value === 'string') {
			const parsed = parseInt(value, 10);
			return isNaN(parsed) ? 0 : parsed;
		}
		return 0;
	};

	const isFeatureUnlimited = (featureKey: string, categoryKey?: string): boolean => {
		const value = getFeatureValue(featureKey, categoryKey);
		if (value === null || value === undefined) {
			return true;
		}
		return value === -1 || value === 'unlimited' || value === '∞';
	};

	const isFeatureNumericEnabled = (
		featureKey: string,
		categoryKey?: string,
		minValue: number = 1,
	): boolean => {
		const value = getFeatureNumericValue(featureKey, categoryKey);
		return value >= minValue || value === -1; // -1 means unlimited
	};

	const getFeatureDisplayText = (featureKey: string, categoryKey?: string): string => {
		if (!features) {
			return '';
		}

		if (categoryKey) {
			const category = features[categoryKey];
			if (!category) {
				return '';
			}

			const feature = category[featureKey];
			return feature?.displayText ?? featureKey;
		}

		// Search across all categories
		for (const category of Object.values(features)) {
			if (category && category[featureKey]) {
				return category[featureKey].displayText ?? featureKey;
			}
		}

		return featureKey;
	};

	const getFeatureTooltip = (featureKey: string, categoryKey?: string): string => {
		if (!features) {
			return '';
		}

		if (categoryKey) {
			const category = features[categoryKey];
			if (!category) {
				return '';
			}

			const feature = category[featureKey];
			return feature?.tooltip ?? '';
		}

		// Search across all categories
		for (const category of Object.values(features)) {
			if (category && category[featureKey]) {
				return category[featureKey].tooltip ?? '';
			}
		}

		return '';
	};

	const hasFeature = (featureKey: string, categoryKey?: string): boolean => {
		if (!features) {
			return false;
		}

		if (categoryKey) {
			const category = features[categoryKey];
			return !!(category && category[featureKey]);
		}

		// Search across all categories
		for (const category of Object.values(features)) {
			if (category && category[featureKey]) {
				return true;
			}
		}

		return false;
	};

	const getCurrentPlan = (): string => {
		return user?.inboundSubscriptionTier || 'pro';
	};

	return {
		features,
		isLoading,
		error,
		isFeatureEnabled,
		getFeatureValue,
		getFeatureDisplayText,
		getFeatureTooltip,
		hasFeature,
		isFeatureUnlimited,
		getFeatureNumericValue,
		isFeatureNumericEnabled,
		getCurrentPlan,
	};
};
